import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Util } from '@libs/utilities/util';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';

import { NgAbstractInputComponent } from '../../shared/inputs.abstract';
import { EnumNgFaStyle } from '../../../../styling-components';
import { INgCheckboxDropdownMultiSelect, INgDropdownOptionsData } from '../checkbox-multi-select-dropdown/checkbox-multi-select-dropdown.interface';


@Component({
    selector: 'ng-checkbox-multi-select-menu',
    templateUrl: './checkbox-multi-select-menu.component.html',
    styleUrls: ['./checkbox-multi-select-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: NgCheckboxMultiSelectMenuComponent,
            multi: true
        }
    ],
    standalone: false
})
export class NgCheckboxMultiSelectMenuComponent extends NgAbstractInputComponent<string[]> implements ControlValueAccessor, OnInit {

	//** Configurations */
	readonly DROPDOWN_ID: string = Util.Random.randomAlphanumericString(16);

	//** Decorators */
	@ViewChild('dropdownBlock') dropdownBlock!: ElementRef<HTMLDivElement>;

	@Input() dropdownWidth 	 : number = 200;
	@Input() dropdownData 	!: INgCheckboxDropdownMultiSelect;
	@Input() dropdownRef 	!: NgbDropdown;

	//** Getter Helpers */
	readonly EnumNgFaStyle: typeof EnumNgFaStyle = EnumNgFaStyle;
	// The menu is portaled under body; fixed positioning avoids a scroll-height offset.
	readonly tooltipPopperOptions = (options: Partial<import('@popperjs/core').Options>): Partial<import('@popperjs/core').Options> => ({ ...options, strategy: 'fixed' });

	constructor() {

		//0 - set the component name
		super('checkbox-multi-select-menu');

		//1 - id value is null then initialize to empty array
		if (Util.Array.isEmpty(this.value!)) this.value = [];
	}


	/**------------------------------------------------------
	 * Angular Live Cycle hook
	 */
	ngOnInit(): void {

		// validate default options
		this.hasValidDefaultOptions();
	}

	//** Select all the options */
	selectAll(): void {

		//0 - deselect already selected options
		this.deselectAll();

		//1 - select the options
		for (const option of this.dropdownData.options) {
			for (const dataOption of option.options) {

				//a. is already maximum options selected?
				this.updateSelected();
				if (this.value!.length >= this.dropdownData.maxSelect) break;

				//b. if not selected then select the option
				dataOption.selected = true;
			}
		}

		//2 - update selected
		this.emitUpdated();
	}

	//** Deselect all the options */
	deselectAll(): void {

		// deselect all the selected options
		for (const dropdown of this.dropdownData.options) {
			for (const option of dropdown.options) {
				option.selected = false;
			}
		}
	}


	/**------------------------------------------------------
	 * Control Value Accessor Functions
	 */
	override writeValue(value: string[]): void {

		//0 - was any value defined?
		if (!value) return;

		//1 - assign the values
		this.value = value;
		for (const dropdownMenu of this.dropdownData.options) {

			//a. are options existing?
			if (Util.Array.isEmpty(dropdownMenu.options)) continue;

			//b. if existing then check when value is selected
			for (const option of dropdownMenu.options) {
				option.selected = value.includes(option.code);
			}
		}
	}


	/**------------------------------------------------------
	 * User Actions
	 */
	updateSelected(): void {

		//0 - get the selected values
		const value: string[] = [];
		for (const checkboxes of this.dropdownData.options) {
			value.push(...checkboxes.options.filter((dropdownOptionsData: INgDropdownOptionsData) => dropdownOptionsData.selected).map((dropdownOptionsData: INgDropdownOptionsData) => dropdownOptionsData.code));
		}

		//1 - trigger the change with the new value
		this.value = value;
	}

	emitUpdated(): void {

		// update the selected options and update changes
		this.updateSelected();
		this.onChange(this.value!);
	}

	//** Restore to default values */
	restoreDefaults(): void {

		//0 - are having valid default options
		if (!this.hasValidDefaultOptions()) return;

		//1 - deselect previously selected options
		this.deselectAll();
		for (const option of this.dropdownData.options) {
			for (const dataOption of option.options) {

				//a. is already maximum options selected?
				this.updateSelected();
				if (this.value!.length >= this.dropdownData.maxSelect) break;

				//b. if not selected then select the option
				if (dataOption.default) dataOption.selected = true;
			}
		}

		//2 - update the selected data and emit
		this.updateSelected();
		this.emitUpdated();
	}


	/**------------------------------------------------------
	 * Helper Functions
	 */
	private hasValidDefaultOptions(): boolean {

		//0 - are default options and maxSelect are in sync
		let totalDefaultOptions: number = 0;
		for (const checkboxes of this.dropdownData.options) {
			totalDefaultOptions += checkboxes.options.filter((dropdownOptionsData: INgDropdownOptionsData) => dropdownOptionsData.default).length;
		}

		//1 - if total default options are less than or equal to maxSelect then valid if not invalid
		if (totalDefaultOptions <= this.dropdownData.maxSelect) return true;
		throw new Error(`CheckboxMultiSelectMenuComponent => hasValidDefaultOptions => FATAL ERROR: total default options should not be more than maxSelect: total default options: ${totalDefaultOptions} - maxSelect options: ${this.dropdownData.maxSelect}`);
	}
}
